import { ConfigurationExpressionsService as CollcalcLanguageServer } from '@festina/collcalc-general-types/collcalc/services/ConfigurationExpressionsService'
import { ConfigurationExpressionsService as ConfigLanguageServer } from '@festina/configuration-general-types/configuration/services/ConfigurationExpressionsService'
import { ConfigurationExpressionsService as LegalEntityLanguageServer } from '@festina/legalentity-general-types/legalentity/services/ConfigurationExpressionsService'
import { ConfigurationExpressionsService as LifeLanguageServer } from '@festina/life-general-types/life/services/ConfigurationExpressionsService'
import { ConfigurationExpressionsService as MoneyInLanguageServer } from '@festina/moneyin-general-types/moneyin/services/ConfigurationExpressionsService'
import { ConfigurationExpressionsService as MoneyOutLanguageServer } from '@festina/moneyout-general-types/moneyout/services/ConfigurationExpressionsService'
import { ConfigurationExpressionsService as UnitLinkLanguageServer } from '@festina/unitlink-general-types/unitlink/services/ConfigurationExpressionsService'
import {
  type ConfigurableServer,
  notEmpty,
  type Optional,
} from '@festina-life/core'
import type { ValidationResult } from '@festina-life/fplus-monaco'

import type { LanguageServerInput } from '../monaco/types/monaco'
import * as LanguageServerWrapper from './FPlusLanguageServerWrapper'
import {
  expressionsAsArray,
  FunctionAssignmentString,
  languageServerInputToEditorInput,
  languageServerInputToEvaluationInput,
  languageServerInputToFormattingInput,
  languageServerInputToTypingInput,
} from './LanguageServerInputUtil'

const ServerServices = {
  collCalc: CollcalcLanguageServer,
  life: LifeLanguageServer,
  unitLink: UnitLinkLanguageServer,
  legalEntity: LegalEntityLanguageServer,
  moneyIn: MoneyInLanguageServer,
  moneyOut: MoneyOutLanguageServer,
  config: ConfigLanguageServer,
} as const

const serverService = (server: Optional<ConfigurableServer>) => {
  if (!server) {
    return ServerServices.config
  }

  const serverKey = server as keyof typeof ServerServices

  if (!serverKey || !ServerServices[serverKey]) {
    throw new Error(`Unknown server: ${serverKey}`)
  }

  return ServerServices[server]
}

export const validate = async (
  input: LanguageServerInput,
): Promise<ValidationResult> => {
  const service = serverService(input.server)

  const result = await LanguageServerWrapper.validate(
    service.fPlusLanguageServicePostValidate({
      requestBody: languageServerInputToTypingInput(input),
    }),
  )

  if (input.prefixFunctionDeclaration) {
    const prefixFunctionDeclarationLength =
      input.prefixFunctionDeclaration.length + FunctionAssignmentString.length
    const transformedDiagnostics = result.diagnostics.map((diagnostic) => {
      if (diagnostic.startLine === 1) {
        const startColumn =
          diagnostic.startColumn - prefixFunctionDeclarationLength
        return {
          ...diagnostic,
          startColumn,
          endColumn:
            diagnostic.endLine === 1
              ? diagnostic.endColumn - prefixFunctionDeclarationLength
              : diagnostic.endColumn,
          message: diagnostic.message?.replace(
            /position : \d+/,
            `position : ${startColumn - 1}`,
          ),
        }
      }
      return diagnostic
    })

    return {
      ...result,
      diagnostics: transformedDiagnostics,
    }
  }

  return result
}

export const fetchScope = async (input: LanguageServerInput) => {
  const service = serverService(input.server)

  return await LanguageServerWrapper.fetchEnvironmnet(
    service.fPlusLanguageServicePostScope({
      requestBody: languageServerInputToTypingInput(input),
    }),
  )
}

export const evaluate = async (input: LanguageServerInput) => {
  const service = serverService(input.server)

  return await LanguageServerWrapper.evaluate(
    service.fPlusLanguageServicePostEvaluate({
      requestBody: languageServerInputToEvaluationInput(input),
    }),
  )
}

export const format = async (input: LanguageServerInput, width: number) => {
  const service = serverService(input.server)

  return await LanguageServerWrapper.format(
    service
      .fPlusLanguageServicePostFormat({
        requestBody: languageServerInputToFormattingInput(input),
        width,
      })
      .then((r) => r?.expressions?.filter(notEmpty) ?? []),
    expressionsAsArray(input.expressions) ?? [],
  )
}

export const suggestions = async (input: LanguageServerInput) => {
  const service = serverService(input.server)

  return await LanguageServerWrapper.suggestions(
    service.fPlusLanguageServicePostSuggestions({
      requestBody: languageServerInputToEditorInput(input),
    }),
  )
}

export const hover = async (input: LanguageServerInput) => {
  const service = serverService(input.server)

  return await LanguageServerWrapper.hover(
    service.fPlusLanguageServicePostHover({
      requestBody: languageServerInputToEditorInput(input),
    }),
  )
}

export const signatureHelp = async (input: LanguageServerInput) => {
  const service = serverService(input.server)

  return await LanguageServerWrapper.signatureHelp(
    service.fPlusLanguageServicePostSignatureHelp({
      requestBody: languageServerInputToEditorInput(input),
    }),
  )
}
